import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { BaseComponent } from '@macs-frontend/core';
import { fromEvent } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  takeUntil,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'md-search-field',
  templateUrl: './search-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFieldComponent extends BaseComponent implements AfterViewInit {
  // @ts-expect-error
  @ViewChild('searchInput') private searchInput: ElementRef<HTMLInputElement>;

  @Input() disabled = false;
  // @ts-expect-error
  @Input() showSearchField: boolean;
  @Input() showFilterButton = false;
  @Input() filterButtonActive?: null | boolean = false;
  @Input() alwaysActive = false;
  /**
   * Classes that will be applied to <form class="search-top" />
   */
  // @ts-expect-error
  @Input() classes:
    | string
    | string[]
    | Set<string>
    | {
        [klass: string]: any;
      };
  @Input()
  get searchQuery(): string {
    return this.searchQueryValue;
  }
  set searchQuery(value: string) {
    this.searchQueryValue = decodeURIComponent(value);
  }

  @Output() toggleSearch: EventEmitter<boolean> = new EventEmitter();
  @Output() search: EventEmitter<string> = new EventEmitter();
  @Output() toggleFilter: EventEmitter<void> = new EventEmitter();
  @Input()
  encodeSearchQuery = true;

  private expandedOnHover = false;
  private searchQueryValue = '';

  onSearch(query: string) {
    this.search.emit(this.encodeSearchQuery ? encodeURIComponent(query) : query);
  }

  toggleSearchField(forceValue = !this.showSearchField) {
    if (forceValue) {
      this.searchInputElement.focus();
    }

    if (forceValue === this.showSearchField) {
      // otherwise it breaks md-dropdown.
      return;
    }

    this.toggleSearch.emit(forceValue);
    this.showSearchField = forceValue;
    // TODO: fix the conflict of several outsideClickDirectives

    // if (!this.showSearchField) {
    //   const searchInput = this.searchInputElement;
    //   if (searchInput) {
    //     searchInput.focus();
    //   }
    // }
  }

  ngAfterViewInit(): void {
    this.attachSearchHandler();
  }

  onOutsideClick() {
    const searchInputElement = this.searchInputElement;
    if (searchInputElement && !this.searchInputElement.value.trim().length) {
      this.searchInputElement.value = '';
    }
    this.toggleSearchField(false);
  }

  clearSearch() {
    const searchInputElement = this.searchInputElement;
    if (searchInputElement) {
      this.searchInputElement.value = '';
      const inputEvent = new Event('input');
      searchInputElement.dispatchEvent(inputEvent);
      this.toggleSearchField(false);
    }
  }

  onSearchHover() {
    if (!this.showSearchField) {
      this.toggleSearchField();
      this.expandedOnHover = true;
      this.searchInputElement.focus();
    }
  }

  onSearchLeave() {
    if (
      this.showSearchField &&
      this.expandedOnHover &&
      this.searchInputElement.value.trim() === ''
    ) {
      this.expandedOnHover = false;
      this.toggleSearchField();
    }
  }

  private attachSearchHandler() {
    const searchInputElement = this.searchInputElement;
    if (searchInputElement) {
      fromEvent(searchInputElement, 'input')
        .pipe(
          debounceTime(300),
          map(this.getTargetValue),
          startWith(''),
          skip(1),
          distinctUntilChanged(),
          filter(this.isNotContiniousWhiteSpace),
          tap(this.onSearch.bind(this)),
          takeUntil(this.destroy$),
        )
        .subscribe();
    }
  }

  // @ts-expect-error
  private get searchInputElement(): HTMLInputElement {
    if (this.searchInput) {
      const searchInputElement = this.searchInput.nativeElement;
      if (searchInputElement) {
        return searchInputElement;
      }
    }
  }

  private getTargetValue(event: Event): string {
    return (event.target as HTMLInputElement).value.trim();
  }

  private isNotContiniousWhiteSpace(value: string) {
    return !(value.length > 0 && value.trim().length === 0);
  }
}
